import { AgentClient } from 'agents/client'
import * as v from 'valibot'
import {
  POLICY_MANAGER_ROOM,
  POLICY_REGISTRY_CHANGED,
} from '@worker/policy-builder/client'
import type { PolicyListFilters } from '@worker/policy-builder/client'
import {
  POLICY_REGISTRY_KEYS,
  policyCountsQuery,
  policyListQuery,
  policyMentionsQuery,
} from '~/queries/policy-studio/policies'

/** A socket frame that is the registry's change nudge; every other frame fails it. */
const RegistryChange = v.pipe(
  v.string(),
  v.parseJson(),
  v.object({ type: v.literal(POLICY_REGISTRY_CHANGED) }),
)

/**
 * One socket per tab to the registry while any registry read is mounted (the
 * studio layout's counts keep it open across the studio). Every write nudges
 * it, and every active registry query refetches, a burst coalescing into one.
 * Reads stay paged and filtered over HTTP; the socket only says when. A
 * reconnect refetches too, for whatever changed while it was down.
 */
const useRegistryChanges = createSharedComposable(() => {
  const queryCache = useQueryCache()
  const refresh = useDebounceFn(
    () => queryCache.invalidateQueries({ key: POLICY_REGISTRY_KEYS.root }),
    250,
  )
  const socket = new AgentClient({
    agent: 'policy-manager-agent',
    name: POLICY_MANAGER_ROOM,
    host: useRuntimeConfig().public.POLICY_BUILDER_HOST,
    query: useCollabAuthQuery(),
  })
  let reconnecting = false
  socket.addEventListener('open', () => {
    if (reconnecting) void refresh()
    reconnecting = true
  })
  socket.addEventListener('message', (event) => {
    if (v.is(RegistryChange, event.data)) void refresh()
  })
  tryOnScopeDispose(() => socket.close())
})

const live = () => {
  if (import.meta.client) useRegistryChanges()
}

/** One filtered, server-paged slice of the policy registry. */
export const usePolicyList = (filters: MaybeRefOrGetter<PolicyListFilters>) => {
  live()
  return useQuery(() => policyListQuery(toValue(filters)))
}

/** Stage tiles and attention counts for the whole registry. */
export const usePolicyCounts = () => {
  live()
  return useQuery(policyCountsQuery)
}

/** Open threads, across policies, that tag any of `tags`. */
export const usePolicyMentions = (tags: MaybeRefOrGetter<string[]>) => {
  live()
  return useQuery(() => policyMentionsQuery(toValue(tags)))
}
